import type { LandingContext } from "./types";
import { trackPageClick } from "./beacon";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/** Reads the landing context off a marker element, or undefined when it is an ordinary embed. */
export function landingContextOf(el: HTMLElement): LandingContext | undefined {
  if (el.dataset.hrnFormsContext !== "landing") return undefined;
  const pageId = el.dataset.hrnFormsPage;
  if (!pageId) return undefined;
  const mode = el.dataset.hrnFormsThankyou;
  return {
    pageId,
    pageVariantId: el.dataset.hrnFormsPageVariant || undefined,
    preview: el.dataset.hrnFormsPreview === "1",
    thankyouMode: mode === "page" || mode === "redirect" ? mode : "inline",
    thankyouUrl: el.dataset.hrnFormsThankyouUrl,
    track: el.dataset.hrnFormsTrack || undefined,
  };
}

/** The label a page button's clicks are counted under: its data-track name, else its text. */
export function buttonLabel(el: HTMLElement): string {
  return (el.dataset.track || el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 60);
}

/**
 * Counts clicks on the page's own buttons: every `class="lp-button"` link (a
 * markdown `{.button}` link or one in custom HTML) and every `data-track` link.
 * The form card counts its own clicks, so anything inside it is skipped.
 */
export function trackPageButtons(apiBase: string, landing: LandingContext): void {
  document.addEventListener("click", (event) => {
    const target = (event.target as Element | null)?.closest?.("a.lp-button, a[data-track]");
    if (!target || target.closest(".hrnf-card")) return;
    const label = buttonLabel(target as HTMLElement);
    if (label) trackPageClick(apiBase, landing.pageId, landing.pageVariantId ?? "", label);
  });
}

/**
 * After a successful submit on a landing page: tell the pixel, then leave for the
 * thank-you page or the redirect. Returns true when the page is navigating away,
 * so the caller can skip the inline success state.
 */
export function afterLandingSubmit(landing: LandingContext): boolean {
  try {
    window.fbq?.("track", "Lead");
  } catch {
    /* the pixel is optional */
  }
  const target = landingTarget(landing, location.search);
  if (!target) return false;
  // A beat for the pixel's request to go out; sendBeacon-backed beacons already have.
  window.setTimeout(() => location.assign(target), 150);
  return true;
}

/**
 * Where a submit goes next. The thank-you page keeps the landing URL's query
 * (UTMs, fbclid) so ad tracking sees the same values on both steps; an external
 * redirect gets the URL exactly as configured. Only https destinations are used.
 */
export function landingTarget(landing: LandingContext, search: string): string | null {
  const url = landing.thankyouUrl;
  if (!url || !/^https:\/\//.test(url)) return null;
  if (landing.thankyouMode === "page") return search && !url.includes("?") ? url + search : url;
  if (landing.thankyouMode === "redirect") return url;
  return null;
}
